import * as React from 'react'

import { observer } from 'mobx-react'

import { ResourceTypeId } from '~/client/src/shared/constants/resourceTypes'
import useEquipmentResources from '~/client/src/shared/hooks/useEquipmentResources'
import useLocationResources from '~/client/src/shared/hooks/useLocationResources'
import useMaterialResources from '~/client/src/shared/hooks/useMaterialResources'
import { useResourceCardSchema } from '~/client/src/shared/hooks/useResourceCardSchema'
import { useResourceRuntimeContext } from '~/client/src/shared/hooks/useResourceRuntimeContext'
import { IResourceRecord } from '~/client/src/shared/resources/definitions/types'
import type { ResourceSchema } from '~/client/src/shared/resources/domain/resourceSchema'

import ResourceSelectionContent from './ResourceSelectionContent'

export type SelectableResourceTypeId =
  | typeof ResourceTypeId.LOCATIONS
  | typeof ResourceTypeId.OFFLOADING_EQUIPMENT
  | typeof ResourceTypeId.MATERIALS

interface IProps {
  resourceTypeId: SelectableResourceTypeId
  projectId: string
  resourceLabel: string
  selectedIds: string[]
  recordPredicate?: (record: IResourceRecord) => boolean
  onApply: (ids: string[]) => void
  onClose: () => void
  allowEmptySelection?: boolean
}

type UseRecords = (config: { projectId?: string }) => {
  records: IResourceRecord[]
  schema: ResourceSchema
}

// One component per type, so each calls its own records hook unconditionally.
const createTypedContent = (useRecords: UseRecords): React.FC<IProps> =>
  observer(({ resourceTypeId, projectId, ...props }: IProps) => {
    const { records, schema } = useRecords({ projectId })
    const runtimeContext = useResourceRuntimeContext()
    const {
      cardConfiguration,
      properties,
      propertiesReady,
      ready,
      getDisplayValue,
    } = useResourceCardSchema({ schema, resourceTypeId, runtimeContext })

    return (
      <ResourceSelectionContent
        {...props}
        resourceTypeId={resourceTypeId}
        records={records}
        cardConfiguration={cardConfiguration}
        properties={properties}
        propertiesReady={propertiesReady}
        loading={!ready}
        getDisplayValue={getDisplayValue}
      />
    )
  })

const CONTENT_BY_TYPE: Record<SelectableResourceTypeId, React.FC<IProps>> = {
  [ResourceTypeId.LOCATIONS]: createTypedContent(useLocationResources),
  [ResourceTypeId.OFFLOADING_EQUIPMENT]: createTypedContent(
    useEquipmentResources,
  ),
  [ResourceTypeId.MATERIALS]: createTypedContent(useMaterialResources),
}

const ResourceTypeSelectionContent: React.FC<IProps> = props => {
  const Content = CONTENT_BY_TYPE[props.resourceTypeId]
  return <Content {...props} />
}

export default ResourceTypeSelectionContent
